// Constantes de Permisos del Sistema
// Define todos los módulos y acciones disponibles en el sistema

/**
 * Roles fijos del sistema
 */
export const FIXED_ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  CLIENT: 'cliente'
}

/**
 * Módulos del sistema
 */
export const MODULES = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  ORDERS: 'orders',
  SALES: 'sales',
  LABELS: 'labels',
  PURCHASE_ORDERS: 'purchase_orders',
  SUPPLIERS: 'suppliers',
  SUPPLIES: 'supplies',
  CUSTOMERS: 'customers',
  USERS: 'users',
  WAREHOUSES: 'warehouses',
  STOCK: 'stock',
  CONFIGURATIONS: 'configurations',
  SECURITY: 'security', // Solo superadmin
  OFFERS: 'offers',
  REPORTS: 'reports'
}

/**
 * Acciones disponibles para cada módulo
 */
export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE: 'manage' // Acción especial que incluye todas las demás
}

/**
 * Definición de permisos por módulo
 * Cada módulo tiene sus acciones disponibles
 */
export const MODULE_PERMISSIONS = {
  [MODULES.PRODUCTS]: {
    label: 'Productos',
    description: 'Gestionar productos del catálogo',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.CATEGORIES]: {
    label: 'Categorías',
    description: 'Gestionar categorías y subcategorías',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.ORDERS]: {
    label: 'Pedidos de Venta',
    description: 'Ver y gestionar pedidos de clientes',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.SALES]: {
    label: 'Ventas',
    description: 'Ver y gestionar ventas realizadas',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.LABELS]: {
    label: 'Etiquetas',
    description: 'Diseñar y generar etiquetas para productos',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.PURCHASE_ORDERS]: {
    label: 'Pedidos de Compra',
    description: 'Ver y gestionar pedidos a proveedores',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.SUPPLIERS]: {
    label: 'Proveedores',
    description: 'Gestionar información de proveedores',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.SUPPLIES]: {
    label: 'Insumos',
    description: 'Gestionar insumos y materiales',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.CUSTOMERS]: {
    label: 'Clientes',
    description: 'Ver y gestionar información de clientes',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.USERS]: {
    label: 'Usuarios',
    description: 'Gestionar usuarios del sistema',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.WAREHOUSES]: {
    label: 'Depósitos',
    description: 'Gestionar depósitos y almacenes',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.STOCK]: {
    label: 'Stock',
    description: 'Gestionar inventario y movimientos de stock',
    actions: [ACTIONS.VIEW, ACTIONS.MANAGE]
  },
  [MODULES.CONFIGURATIONS]: {
    label: 'Configuraciones',
    description: 'Acceder a configuraciones del sistema',
    actions: [ACTIONS.VIEW, ACTIONS.EDIT]
  },
  [MODULES.SECURITY]: {
    label: 'Seguridad',
    description: 'Gestionar roles y permisos (solo superadmin)',
    actions: [ACTIONS.MANAGE]
  },
  [MODULES.OFFERS]: {
    label: 'Ofertas',
    description: 'Gestionar ofertas y promociones',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE]
  },
  [MODULES.REPORTS]: {
    label: 'Reportes',
    description: 'Ver reportes y estadísticas',
    actions: [ACTIONS.VIEW]
  }
}

/**
 * Verificar si un rol tiene un permiso específico
 * @param {Object} role - Objeto del rol (puede ser fijo o personalizado)
 * @param {string} module - Módulo a verificar
 * @param {string} action - Acción a verificar
 * @returns {boolean}
 */
export const hasPermission = (role, module, action) => {
  // Superadmin tiene todos los permisos
  if (role === FIXED_ROLES.SUPERADMIN) {
    return true
  }

  // Admin tiene todos los permisos excepto usuarios y seguridad
  if (role === FIXED_ROLES.ADMIN) {
    if (module === MODULES.USERS || module === MODULES.SECURITY) {
      return false
    }
    return true
  }

  // Cliente no tiene acceso al admin
  if (role === FIXED_ROLES.CLIENT) {
    return false
  }

  // Para roles personalizados, verificar permisos
  if (typeof role === 'object' && role.permissions) {
    const modulePermissions = role.permissions[module]
    if (!modulePermissions) return false

    // Si tiene MANAGE, tiene todos los permisos
    if (modulePermissions.includes(ACTIONS.MANAGE)) {
      return true
    }

    // Verificar la acción específica
    return modulePermissions.includes(action)
  }

  return false
}

/**
 * Obtener todos los módulos disponibles
 * @returns {Array} Array de módulos con su información
 */
export const getAllModules = () => {
  return Object.keys(MODULE_PERMISSIONS).map(key => ({
    key,
    ...MODULE_PERMISSIONS[key]
  }))
}

